import { create } from 'zustand';
import apiClient from '../service/apiClient';
import {
    CssFont, 
    FontTemplateResponse, 
    FontTemplateDetailResponse,
    CssFontRequest, 
    CssFontCreateRequest,
    FontRequestMultiple,
    BasicFontRequest,
    FontUpdate,
    FontUpdateOrCreate
} from '../type/cssTemplate';

/**
 * @author pjh
 * @date 2025.08.19
 * @description 폰트 템플릿 관리 스토어
 */

interface FontTemplateStoreData {
    cssTemplate: FontTemplateDetailResponse | null;  // 새로운 상세 응답 타입으로 변경
    loading: boolean;
    selectedImages: Map<string, CssFont>; // cssName을 key로 선택된 이미지 관리
}

interface CssTemplateStoreAction {
    // 기본 템플릿 또는 프랜차이즈 전용 템플릿 조회
    getCssTemplate: (productId: string) => Promise<FontTemplateDetailResponse>;

    // 템플릿 저장 (프랜차이즈 전용 템플릿 생성/수정 및 신규 이미지 등록)
    saveCssTemplate: (request: FontUpdate[], productId: string) => Promise<void>;

    saveBasicCssTemplate: (data: FontUpdateOrCreate[], productId: string) => Promise<void>;
    
}

export const useFontTemplateStore = create<FontTemplateStoreData & CssTemplateStoreAction>((set, get) => ({
    // 초기 상태
    cssTemplate: null,
    loading: false,
    selectedImages: new Map(),
    
    // 기본 템플릿 또는 프랜차이즈 전용 템플릿 조회
    getCssTemplate: async (productId?: string) => {
        set({ loading: true });
        try {
            const response = await apiClient.get(`/css/font/${productId}`);
            const template = response.data;
            
            set({ 
                cssTemplate: template,
                loading: false 
            });
            
            // 기존 이미지들을 selectedFonts에 설정
            const selectedFonts = new Map<string, CssFont>();
            if (template.fonts) {
                template.fonts.forEach((font: CssFont) => {
                    selectedFonts.set(font.cssName, font);
                });
            }
            // set({ selectedFonts });
            
            return template;
        } catch (error) {
            console.error('폰트 템플릿 조회 실패:', error);
            set({ loading: false });
            throw error;
        }
    },

    // 템플릿 저장 (프랜차이즈 전용 템플릿 생성/수정 및 신규 이미지 등록)
    saveCssTemplate: async (request: FontUpdate[], productId: string) => {
        set({ loading: true });
        try {
            await apiClient.post(`/css/font/${productId}`, request);

            set({ loading: false });
        } catch (error) {
            console.error('폰트 템플릿 저장 실패:', error);
            set({ loading: false });
            throw error;
        }
    },

    // 템플릿 저장 (관리자 전용 CSS 신규 폰트 등록)
    saveBasicCssTemplate: async (data: FontUpdateOrCreate[], productId: string) => {
        set({ loading: true });
        try {
            await apiClient.post(`/css/font/basic/${productId}`, data);

            set({ loading: false });
        } catch (error) {
            console.error('폰트 템플릿 저장 실패:', error);
            set({ loading: false });
            throw error;
        }
    }
}));